// Manufacturer listing returns `items` as a numeric count; individual order fetches return an array of line-items.
export function unitsFromOrder(o) {
  if (!o) return 0;
  if (typeof o.items === "number" && Number.isFinite(o.items) && o.items >= 0) return o.items;
  if (Array.isArray(o.items)) return o.items.reduce((s, i) => s + (i?.quantity || 0), 0);
  return 0;
}

export function orderLineItems(o) {
  return Array.isArray(o?.items) ? o.items : [];
}

// Strip the OrientDB record-id prefix so "#96:278586" and "96:278586" compare
// equal. SSE `peptide-order-update` payloads carry the raw `@rid` (with #),
// while the REST API the UI loads with exposes the same value as `id` (no #).
function stripRecordIdPrefix(value) {
  return String(value ?? "").replace(/^#/, "");
}

// True when a live `peptide-order-update` SSE payload refers to the order the
// screen is currently showing. The payload uses `@rid` ("#96:278586") and omits
// the plain `id` the UI was loaded with, so we normalize both sides before
// comparing. Used by PeptideOrderDetail / PeptideOrderFulfillment to refetch
// only when the update is for the open order.
export function peptideOrderUpdateMatchesId(incomingOrder, openOrderId) {
  if (!incomingOrder || openOrderId == null) return false;
  const incomingId = stripRecordIdPrefix(incomingOrder.id ?? incomingOrder["@rid"]);
  const openId = stripRecordIdPrefix(openOrderId);
  return incomingId !== "" && incomingId === openId;
}

// Peptide order vertices do NOT persist a plain `id` — it is always derived
// from the record identity, and only the read/create processes bother to stamp
// it (`Peptide Order - Get_PeptideGetScript.js` does
// `filteredOrder.id = order.getIdentity()...`; the manufacturer Create script
// does the same after its own toMap). Every WRITE process returns the order
// through `toMap(true)` — which carries `@rid` ("#96:280669") but no `id` — or
// through `toMap(false)`, which carries NEITHER. The give-away is that those
// same scripts hand-stamp `notifOrder.id = orderId` for their notification
// payloads precisely because the map lacks one.
//
// Callers read `order.id` for the next comment send, attachment-open, and the
// order-number header, and the payment/approval flows assert
// `order.id === expectedOrderId` before treating a write as authoritative.
// Without normalization those assertions can only ever fail, which turns a
// SUCCESSFUL charge/upload/approval into a user-facing error.
//
// Recovery order: keep an `id` the BE did supply, else derive it from `@rid`,
// else fall back to the id the caller addressed the write to. The fallback is
// safe because these processes are addressed BY order id — the response is for
// that order by construction — and it keeps the *meaningful* half of the
// caller's assertion (status / payerType / parentOrderId, all genuinely
// server-supplied) doing real work. Returns the input unchanged when there's
// no recoverable id, so callers' existing `if (!order)` guards still fire on a
// genuinely empty body.
export function ensureOrderId(order, fallbackId) {
  if (!order || order.id != null) return order;
  if (order["@rid"] != null) return { ...order, id: stripRecordIdPrefix(order["@rid"]) };
  const fallback = stripRecordIdPrefix(fallbackId);
  return fallback === "" ? order : { ...order, id: fallback };
}
